import { useState, useEffect, useCallback, Fragment, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { apiUrl } from "@/lib/api-client";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { hapticTap } from "@/lib/haptics";
import { Link } from "react-router-dom";
import { Loader2, ChevronLeft, ChevronRight, ChevronDown, CheckSquare, X, Minus, FlaskConical, Trash2, RefreshCw, Filter } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { MoreHorizontal } from "lucide-react";
import WebhookHistory from "@/components/mappings/WebhookHistory";
import LeadListPanel from "@/components/mappings/LeadListPanel";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const PAGE_SIZE = 25;
const EVENT_TYPES = [
  "quote_success",
  "quote_deal_no_sf",
  "no_match",
  "size_too_small",
  "size_too_large",
  "validation_error",
  "server_error",
] as const;
const SOURCES = ["embed", "facebook", "admin", "bulk_test"] as const;
const REVIEW_FILTERS = ["all", "pending", "correct", "incorrect"] as const;

const SURFACE_OPTIONS = ["woodgrain", "decocolor", "planar", "slategrain", "micrograin", "metallic"] as const;

interface ConfiguratorLogRow {
  id: string;
  created_at: string;
  source: string;
  event_type: string;
  width: number | null;
  height: number | null;
  motiv: string | null;
  surface: string | null;
  color: string | null;
  electric_drive: boolean | null;
  contact_id: string | null;
  email: string | null;
  deal_id: string | null;
  deal_title?: string | null;
  sf_document_id: string | null;
  result_type: string | null;
  matched_width: number | null;
  matched_height: number | null;
  solution_summary: string | null;
  error_message: string | null;
  quote_email_sent?: boolean | null;
  rule_steps?: { step: string; description: string; explanation?: string; widthRange?: number[]; heightRange?: number[]; result: string; matched_width?: number; matched_height?: number; score?: number; candidates?: { sirka: number; vyska: number; score: number; wDist: number; hDist: number; wPenalty: number; hPenalty: number }[] }[] | null;
  review_status?: string | null;
  review_note?: string | null;
  quote_items?: { item_name: string; price?: number; quantity?: number; quantity_text?: string; comment?: string; sku?: string; tax?: number }[] | null;
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString("sk-SK", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

/** Egyezik a worker `AI_EMPTY_NOTE_KEY` értékével. */
const AI_EMPTY_NOTE_KEY = "empty_note";

function formatAiCategoryDisplay(
  key: string | null | undefined,
  labels: Record<string, string>,
  t: (k: string, def: string) => string
): string {
  if (key == null || key === "") return "—";
  if (key === AI_EMPTY_NOTE_KEY) return t("priceGenerator.aiCategoryEmptyNote", "Üres megjegyzés");
  return labels[key] ?? key;
}

interface AiClassificationRow {
  id: string;
  deal_id: string;
  deal_title?: string | null;
  /** Server: deal.lead_source === price_generator_tester */
  display_deal_as_embed?: boolean;
  note_text: string;
  ai_category: string | null;
  ai_confidence: number | null;
  ai_reasoning: string | null;
  human_category: string | null;
  review_status?: string | null;
  review_note?: string | null;
  created_at: string;
}

function AiClassificationsLogContent() {
  const { t } = useTranslation();
  const [items, setItems] = useState<AiClassificationRow[]>([]);
  const [allowedHuman, setAllowedHuman] = useState<string[]>([]);
  const [categoryLabels, setCategoryLabels] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [reviewFilter, setReviewFilter] = useState<string>("");
  const [expandedAiRowId, setExpandedAiRowId] = useState<string | null>(null);
  const [aiReviewError, setAiReviewError] = useState<string | null>(null);
  const pageSize = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const offset = (page - 1) * pageSize;
      const params = new URLSearchParams();
      params.set("limit", String(pageSize));
      params.set("offset", String(offset));
      if (reviewFilter) params.set("review_status", reviewFilter);
      const res = await fetch(apiUrl(`/api/price-generator/ai-classifications?${params}`), { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        if (body.error === "migration_needed" && typeof body.message === "string") {
          setAiReviewError(body.message);
        }
        return;
      }
      const data = await res.json();
      setAiReviewError(null);
      setItems(data.items ?? []);
      setTotal(typeof data.count === "number" ? data.count : (data.items?.length ?? 0));
      setAllowedHuman(Array.isArray(data.allowed_human_categories) ? data.allowed_human_categories : []);
      const cl = data.category_labels;
      setCategoryLabels(cl && typeof cl === "object" && !Array.isArray(cl) ? (cl as Record<string, string>) : {});
    } catch (e) {
      console.error("[ai-classifications]", e);
    } finally {
      setLoading(false);
    }
  }, [page, reviewFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const patchHuman = async (id: string, human_category: string | null) => {
    const res = await fetch(apiUrl(`/api/price-generator/ai-classifications/${id}`), {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ human_category }),
    });
    if (res.ok) {
      setItems((prev) => prev.map((r) => (r.id === id ? { ...r, human_category } : r)));
    }
  };

  const patchAiReviewStatus = async (id: string, review_status: string | null) => {
    setItems((prev) => prev.map((r) => (r.id === id ? { ...r, review_status } : r)));
    try {
      const res = await fetch(apiUrl(`/api/price-generator/ai-classifications/${id}`), {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ review_status }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        if (body.error === "migration_needed" && typeof body.message === "string") {
          setAiReviewError(body.message);
        }
        setItems((prev) => prev.map((r) => (r.id === id ? { ...r, review_status: null } : r)));
      } else {
        setAiReviewError(null);
      }
    } catch (e) {
      console.error("[ai-classifications] review_status", e);
      void load();
    }
  };

  const patchAiReviewNote = async (id: string, note: string) => {
    setItems((prev) => prev.map((r) => (r.id === id ? { ...r, review_note: note || null } : r)));
    try {
      const res = await fetch(apiUrl(`/api/price-generator/ai-classifications/${id}`), {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ review_note: note || null }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        if (body.error === "migration_needed" && typeof body.message === "string") {
          setAiReviewError(body.message);
        }
      }
    } catch (e) {
      console.error("[ai-classifications] review_note", e);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <Card className="min-w-0">
      <CardHeader>
        <CardTitle className="text-lg sm:text-2xl">
          {t("priceGenerator.aiClassTitle", "AI besorolások")}
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          {t("priceGenerator.aiClassSubtitle", "Napló: megjegyzés, AI eredmény, kézi korrekció (nem mozgat deal-et).")}
        </p>
      </CardHeader>
      <CardContent className="min-w-0 space-y-4">
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-4 items-stretch sm:items-end">
          <div className="space-y-1 w-full sm:w-auto sm:min-w-0">
            <Label className="text-xs">{t("priceGenerator.logReviewLabel", "Értékelés")}</Label>
            <Select value={reviewFilter || "all"} onValueChange={(v) => { setReviewFilter(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger className="w-full sm:w-40 max-w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REVIEW_FILTERS.map((rf) => (
                  <SelectItem key={rf} value={rf}>
                    {t(`priceGenerator.logReviewFilter.${rf}`, rf === "all" ? "Összes" : rf === "pending" ? "Nem jelölt" : rf === "correct" ? "Helyes" : "Hibás")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {aiReviewError && (
          <div className="rounded-md border border-orange-300 bg-orange-50 dark:bg-orange-900/20 dark:border-orange-700 p-3 text-sm text-orange-800 dark:text-orange-200 flex items-start gap-2">
            <span className="shrink-0 mt-0.5">⚠️</span>
            <div>
              <p className="font-medium">{t("priceGenerator.logReviewMigrationNeeded", "Migráció szükséges")}</p>
              <p className="text-xs mt-0.5">{aiReviewError}</p>
            </div>
            <button type="button" onClick={() => setAiReviewError(null)} className="ml-auto text-orange-600 hover:text-orange-800 dark:text-orange-300">
              ×
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6">{t("priceGenerator.aiClassEmpty", "Nincs még besorolási napló.")}</p>
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10 p-1" />
                  <TableHead className="text-xs whitespace-nowrap">{t("priceGenerator.aiColTime", "Idő")}</TableHead>
                  <TableHead className="text-xs">{t("priceGenerator.aiColDeal", "Ügylet")}</TableHead>
                  <TableHead className="text-xs min-w-[160px]">{t("priceGenerator.aiColNote", "Megjegyzés")}</TableHead>
                  <TableHead className="text-xs">{t("priceGenerator.aiColAiCat", "AI kategória")}</TableHead>
                  <TableHead className="text-xs">{t("priceGenerator.aiColConf", "%")}</TableHead>
                  <TableHead className="text-xs min-w-[180px]">{t("priceGenerator.aiColReason", "Indoklás")}</TableHead>
                  <TableHead className="text-xs min-w-[140px]">{t("priceGenerator.aiColHuman", "Kézi")}</TableHead>
                  <TableHead className="text-xs w-16">{t("priceGenerator.logReviewLabel", "Értékelés")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((row) => (
                  <Fragment key={row.id}>
                    <TableRow
                      className={
                        row.review_status === "correct"
                          ? "bg-green-50/50 dark:bg-green-900/10"
                          : row.review_status === "incorrect"
                            ? "bg-red-50/50 dark:bg-red-900/10"
                            : ""
                      }
                    >
                      <TableCell className="w-10 p-1 align-middle">
                        <button
                          type="button"
                          onClick={() => setExpandedAiRowId((id) => (id === row.id ? null : row.id))}
                          className="min-h-9 min-w-9 inline-flex items-center justify-center rounded hover:bg-muted"
                          aria-label={
                            expandedAiRowId === row.id
                              ? t("priceGenerator.aiReviewCollapseNote")
                              : t("priceGenerator.aiReviewExpandNote")
                          }
                        >
                          {expandedAiRowId === row.id ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap">{formatDate(row.created_at)}</TableCell>
                      <TableCell className="text-xs">
                        {row.display_deal_as_embed ? (
                          <span className="text-muted-foreground">{t("priceGenerator.logSourceValue.embed")}</span>
                        ) : row.deal_id ? (
                          <Link to={`/crm/deals/${row.deal_id}`} className="text-primary underline">
                            {row.deal_title || row.deal_id.slice(0, 8)}
                          </Link>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className="text-xs max-w-[240px]">
                        <span className="line-clamp-3 whitespace-pre-wrap break-words">{row.note_text || "—"}</span>
                      </TableCell>
                      <TableCell className="text-xs">
                        {formatAiCategoryDisplay(row.ai_category, categoryLabels, t)}
                      </TableCell>
                      <TableCell className="text-xs">{row.ai_confidence ?? "—"}</TableCell>
                      <TableCell className="text-xs max-w-[280px]">
                        <span className="line-clamp-4 whitespace-pre-wrap break-words">{row.ai_reasoning ?? "—"}</span>
                      </TableCell>
                      <TableCell className="text-xs p-1">
                        <Select
                          value={row.human_category === null || row.human_category === "" ? "__none__" : row.human_category}
                          onValueChange={(v) => patchHuman(row.id, v === "__none__" ? null : v)}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue placeholder="—" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">{t("priceGenerator.aiHumanNone", "(nincs)")}</SelectItem>
                            {allowedHuman.map((k) => (
                              <SelectItem key={k} value={k}>
                                {formatAiCategoryDisplay(k, categoryLabels, t)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <ReviewButtons
                          reviewStatus={row.review_status}
                          onToggle={(status) => patchAiReviewStatus(row.id, status)}
                        />
                      </TableCell>
                    </TableRow>
                    {expandedAiRowId === row.id && (
                      <TableRow className="bg-muted/30">
                        <TableCell colSpan={9} className="p-4">
                          <div className="text-xs font-medium mb-2">{t("priceGenerator.aiReviewNoteSection")}</div>
                          <ReviewNoteEditor
                            reviewNote={row.review_note}
                            onSave={(note) => patchAiReviewNote(row.id, note)}
                            placeholder={t("priceGenerator.aiReviewFeedbackPlaceholder")}
                            addNoteLabel={t("priceGenerator.aiReviewFeedbackAdd")}
                            expectedLabel={t("priceGenerator.aiReviewFeedbackLabel")}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 pt-4">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-xs text-muted-foreground">
              {page} / {totalPages}
            </span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ReviewButtons({
  reviewStatus,
  onToggle,
}: {
  reviewStatus: string | null | undefined;
  onToggle: (status: string | null) => void;
}) {
  const current = reviewStatus;
  return (
    <div className="flex items-center gap-0.5">
      <button
        type="button"
        onClick={() => onToggle(current === "correct" ? null : "correct")}
        className={`min-h-9 min-w-9 inline-flex items-center justify-center rounded ${current === "correct" ? "bg-green-100 dark:bg-green-900/40 text-green-600" : "text-muted-foreground/40 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"}`}
        title="Helyes"
      >
        <CheckSquare className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={() => onToggle(current === "incorrect" ? null : "incorrect")}
        className={`min-h-9 min-w-9 inline-flex items-center justify-center rounded ${current === "incorrect" ? "bg-red-100 dark:bg-red-900/40 text-red-600" : "text-muted-foreground/40 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"}`}
        title="Hibás"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function ReviewNoteEditor({
  reviewNote,
  onSave,
  placeholder,
  addNoteLabel,
  expectedLabel,
}: {
  reviewNote: string | null | undefined;
  onSave: (note: string) => void;
  placeholder?: string;
  addNoteLabel?: string;
  expectedLabel?: string;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(reviewNote ?? "");

  const handleSave = () => {
    onSave(value);
    setEditing(false);
  };

  const addLabel = addNoteLabel ?? t("priceGenerator.logReviewAddExpected", "Elvárt eredmény megadása");
  const expectedLbl = expectedLabel ?? t("priceGenerator.logReviewExpected", "Elvárt");
  const inputPlaceholder =
    placeholder ?? t("priceGenerator.logReviewExpectedPlaceholder", "pl. 3750×3000, túl nagy méret, ne generáljon kaput...");

  if (!editing && !reviewNote) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
      >
        + {addLabel}
      </button>
    );
  }

  if (!editing) {
    return (
      <div className="flex items-start gap-2">
        <div className="text-xs">
          <span className="text-muted-foreground font-medium">{expectedLbl}:</span>{" "}
          <span>{reviewNote}</span>
        </div>
        <button
          type="button"
          onClick={() => { setValue(reviewNote ?? ""); setEditing(true); }}
          className="text-xs text-muted-foreground hover:text-foreground shrink-0"
        >
          ✏️
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={inputPlaceholder}
        className="h-7 text-xs flex-1"
        onKeyDown={(e) => { if (e.key === "Enter") handleSave(); if (e.key === "Escape") setEditing(false); }}
        autoFocus
      />
      <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={handleSave}>
        {t("common.save", "Mentés")}
      </Button>
      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setEditing(false)}>
        ×
      </Button>
    </div>
  );
}

export default function ConfiguratorLogTab() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [logSubTab, setLogSubTab] = useState<"configurator" | "ai" | "facebook_webhooks">("configurator");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [eventType, setEventType] = useState<string>("");
  const [source, setSource] = useState<string>("");
  const [email, setEmail] = useState("");
  const [reviewFilter, setReviewFilter] = useState<string>("");
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<ConfiguratorLogRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectionModeEnabled, setSelectionModeEnabled] = useState(false);
  const longPressTimersRef = useRef<Map<string, number>>(new Map());
  const longPressStartRef = useRef<Map<string, { x: number; y: number }>>(new Map());

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkCount, setBulkCount] = useState("100");
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResult, setBulkResult] = useState<{
    generated: number;
    matched: number;
    noMatch: number;
    insertFailed?: number;
    firstInsertError?: string | null;
    noMatchBreakdown?: { no_match: number; size_too_small: number; size_too_large: number };
  } | null>(null);

  const [deleteLogOpen, setDeleteLogOpen] = useState(false);
  const [deleteLogRunning, setDeleteLogRunning] = useState(false);
  const [deleteSelectedOpen, setDeleteSelectedOpen] = useState(false);
  const [deleteSelectedRunning, setDeleteSelectedRunning] = useState(false);

  const [validateOpen, setValidateOpen] = useState(false);
  const [validateRunning, setValidateRunning] = useState(false);
  const [logFiltersSheetOpen, setLogFiltersSheetOpen] = useState(false);
  const [validateResult, setValidateResult] = useState<{
    summary: { total: number; correct_reviewed: number; incorrect_reviewed: number; changed: number; now_correct: number; now_incorrect: number; still_correct: number; still_incorrect: number };
    results: { id: string; width: number; height: number; review_status: string; review_note: string | null; original: { matched_width: number | null; matched_height: number | null; result_type: string | null }; recalculated: { matched_width: number | null; matched_height: number | null; result_type: string | null }; changed: boolean; verdict: string }[];
  } | null>(null);

  const fetchLog = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String((page - 1) * PAGE_SIZE));
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      if (eventType) params.set("event_type", eventType);
      if (source) params.set("source", source);
      if (email.trim()) params.set("email", email.trim());
      if (reviewFilter) params.set("review_status", reviewFilter);

      const res = await fetch(apiUrl(`/api/price-generator/log?${params}`), {
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setItems(data.items ?? []);
      setTotal(data.total ?? 0);
    } catch (e) {
      console.error(e);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, from, to, eventType, source, email, reviewFilter]);

  useEffect(() => {
    fetchLog();
  }, [fetchLog]);

  const [reviewError, setReviewError] = useState<string | null>(null);

  const handleReviewNoteSave = async (id: string, note: string) => {
    setItems((prev) => prev.map((r) => (r.id === id ? { ...r, review_note: note || null } : r)));
    try {
      const res = await fetch(apiUrl(`/api/price-generator/log/${id}/review`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ review_note: note || null }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ message: res.statusText }));
        if (body.error === "migration_needed") {
          setReviewError(body.message);
        }
      }
    } catch (e) {
      console.error("Review note save failed:", e);
    }
  };

  const handleReviewUpdate = async (id: string, status: string | null) => {
    setItems((prev) => prev.map((r) => (r.id === id ? { ...r, review_status: status } : r)));
    try {
      const res = await fetch(apiUrl(`/api/price-generator/log/${id}/review`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ review_status: status }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ message: res.statusText }));
        if (body.error === "migration_needed") {
          setReviewError(body.message);
        } else {
          setReviewError(body.message || "Review mentése sikertelen.");
        }
        setItems((prev) => prev.map((r) => (r.id === id ? { ...r, review_status: null } : r)));
        return;
      }
      setReviewError(null);
    } catch (e) {
      console.error("Review update failed:", e);
      setReviewError("Review mentése sikertelen. Hálózati hiba.");
      fetchLog();
    }
  };

  const handleBulkTest = async () => {
    setBulkRunning(true);
    setBulkResult(null);
    try {
      const res = await fetch(apiUrl("/api/price-generator/bulk-test"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          sampleCount: Math.min(500, Math.max(1, Number(bulkCount) || 100)),
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setBulkResult(data);
      fetchLog();
    } catch (e) {
      console.error("Bulk test failed:", e);
    } finally {
      setBulkRunning(false);
    }
  };

  const handleDeleteLog = async () => {
    setDeleteLogRunning(true);
    try {
      const res = await fetch(apiUrl("/api/price-generator/log"), { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      setDeleteLogOpen(false);
      setSelectedIds(new Set());
      fetchLog();
    } catch (e) {
      console.error("Delete log failed:", e);
    } finally {
      setDeleteLogRunning(false);
    }
  };

  const exitSelectionMode = useCallback(() => {
    hapticTap(12);
    setSelectionModeEnabled(false);
    setSelectedIds(new Set());
  }, []);

  const beginRowLongPress = useCallback(
    (rowId: string, e: React.PointerEvent) => {
      if (!isMobile) return;
      if (selectionModeEnabled) return;
      if (e.button !== 0) return;
      longPressStartRef.current.set(rowId, { x: e.clientX, y: e.clientY });
      const tId = window.setTimeout(() => {
        hapticTap(12);
        setSelectionModeEnabled(true);
        setSelectedIds(new Set([rowId]));
      }, 420);
      longPressTimersRef.current.set(rowId, tId);
    },
    [isMobile, selectionModeEnabled],
  );

  const cancelRowLongPress = useCallback((rowId: string) => {
    const tId = longPressTimersRef.current.get(rowId);
    if (tId != null) window.clearTimeout(tId);
    longPressTimersRef.current.delete(rowId);
    longPressStartRef.current.delete(rowId);
  }, []);

  const moveRowLongPress = useCallback(
    (rowId: string, e: React.PointerEvent) => {
      const start = longPressStartRef.current.get(rowId);
      if (!start) return;
      const dx = Math.abs(e.clientX - start.x);
      const dy = Math.abs(e.clientY - start.y);
      if (dx > 12 || dy > 12) cancelRowLongPress(rowId);
    },
    [cancelRowLongPress],
  );

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllOnPage = (checked: boolean) => {
    if (checked) {
      setSelectedIds((prev) => new Set([...prev, ...items.map((r) => r.id)]));
    } else {
      setSelectedIds((prev) => {
        const pageIds = new Set(items.map((r) => r.id));
        const next = new Set(prev);
        pageIds.forEach((id) => next.delete(id));
        return next;
      });
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    setDeleteSelectedRunning(true);
    try {
      const res = await fetch(apiUrl("/api/price-generator/log"), {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ids: Array.from(selectedIds) }),
      });
      if (!res.ok) throw new Error(await res.text());
      setDeleteSelectedOpen(false);
      setSelectedIds(new Set());
      fetchLog();
    } catch (e) {
      console.error("Delete selected failed:", e);
    } finally {
      setDeleteSelectedRunning(false);
    }
  };

  const allOnPageSelected = items.length > 0 && items.every((r) => selectedIds.has(r.id));
  const someOnPageSelected = items.some((r) => selectedIds.has(r.id));

  const handleValidateReviews = async () => {
    setValidateRunning(true);
    setValidateResult(null);
    try {
      const res = await fetch(apiUrl("/api/price-generator/validate-reviews"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setValidateResult(data);
    } catch (e) {
      console.error("Validation failed:", e);
    } finally {
      setValidateRunning(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const applyLogFilters = () => {
    setPage(1);
    if (isMobile) setLogFiltersSheetOpen(false);
  };

  const renderConfiguratorLogFilterFields = () => (
    <>
      <div className="space-y-1 w-full sm:w-auto sm:min-w-0">
        <Label className="text-xs">{t("priceGenerator.logFrom", "Dátum od")}</Label>
        <Input
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          className="w-full sm:w-40 max-w-full"
        />
      </div>
      <div className="space-y-1 w-full sm:w-auto sm:min-w-0">
        <Label className="text-xs">{t("priceGenerator.logTo", "Dátum do")}</Label>
        <Input
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          className="w-full sm:w-40 max-w-full"
        />
      </div>
      <div className="space-y-1 w-full sm:w-auto sm:min-w-0">
        <Label className="text-xs">{t("priceGenerator.logEventType", "Esemény")}</Label>
        <Select value={eventType || "all"} onValueChange={(v) => setEventType(v === "all" ? "" : v)}>
          <SelectTrigger className="w-full sm:w-48 max-w-full">
            <SelectValue placeholder={t("priceGenerator.logAllEvents", "Összes")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("priceGenerator.logAllEvents", "Összes")}</SelectItem>
            {EVENT_TYPES.map((et) => (
              <SelectItem key={et} value={et}>
                {t(`priceGenerator.logEvent.${et}`, et)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1 w-full sm:w-auto sm:min-w-0">
        <Label className="text-xs">{t("priceGenerator.logSource", "Forrás")}</Label>
        <Select value={source || "all"} onValueChange={(v) => setSource(v === "all" ? "" : v)}>
          <SelectTrigger className="w-full sm:w-32 max-w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("priceGenerator.logAllSources", "Összes")}</SelectItem>
            {SOURCES.map((s) => (
              <SelectItem key={s} value={s}>
                {t(`priceGenerator.logSourceValue.${s}`, s)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1 w-full sm:w-auto sm:min-w-0">
        <Label className="text-xs">{t("priceGenerator.logReviewLabel", "Értékelés")}</Label>
        <Select value={reviewFilter || "all"} onValueChange={(v) => setReviewFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-full sm:w-36 max-w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {REVIEW_FILTERS.map((rf) => (
              <SelectItem key={rf} value={rf}>
                {t(`priceGenerator.logReviewFilter.${rf}`, rf === "all" ? "Összes" : rf === "pending" ? "Nem jelölt" : rf === "correct" ? "Helyes" : "Hibás")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1 w-full sm:w-auto sm:min-w-0 flex-1 sm:flex-initial">
        <Label className="text-xs">{t("priceGenerator.logEmail", "Email")}</Label>
        <Input
          type="text"
          placeholder={t("priceGenerator.logEmailPlaceholder", "Keresés emailben")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full sm:w-48 max-w-full"
        />
      </div>
    </>
  );

  return (
    <Tabs
      value={logSubTab}
      onValueChange={(v) => setLogSubTab(v as "configurator" | "ai" | "facebook_webhooks")}
      className="w-full min-w-0"
    >
      <TabsList
        className={cn(
          "mb-3 h-auto min-h-9 w-full min-w-0 gap-1 p-1",
          isMobile
            ? "inline-flex flex-nowrap justify-start"
            : "inline-flex max-w-2xl sm:grid sm:grid-cols-3 sm:justify-stretch",
        )}
      >
        <TabsTrigger
          value="configurator"
          className={cn("shrink-0 text-xs sm:text-sm", isMobile && "max-w-[46vw] whitespace-normal px-2 py-1.5 text-center leading-tight")}
        >
          {t("priceGenerator.logSubTabConfigurator", "Konfigurátor log")}
        </TabsTrigger>
        <TabsTrigger
          value="ai"
          className={cn("shrink-0 text-xs sm:text-sm", isMobile && "max-w-[46vw] whitespace-normal px-2 py-1.5 text-center leading-tight")}
        >
          {t("priceGenerator.logSubTabAi", "AI besorolások")}
        </TabsTrigger>
        <TabsTrigger
          value="facebook_webhooks"
          className={cn("shrink-0 text-xs sm:text-sm", isMobile && "max-w-[46vw] whitespace-normal px-2 py-1.5 text-center leading-tight")}
        >
          {t("priceGenerator.logSubTabFacebookWebhooks", "Facebook leadek & Webhookok")}
        </TabsTrigger>
      </TabsList>
      <TabsContent value="configurator" className="mt-0 border-0 p-0 shadow-none outline-none">
    <Card className="min-w-0">
      <CardHeader className="flex flex-col gap-3 space-y-0 sm:flex-row sm:items-center sm:justify-between">
        <CardTitle className="hidden text-lg sm:block sm:text-2xl">
          {t("priceGenerator.logTabTitle", "Konfigurátor log")}
        </CardTitle>
        <div className="flex w-full flex-wrap items-center justify-start gap-2 sm:w-auto sm:justify-end">
          {isMobile && logSubTab === "configurator" ? (
            <Button
              variant="outline"
              size="sm"
              className="h-9 w-9 shrink-0 p-0"
              type="button"
              onClick={() => {
                hapticTap(12);
                setLogFiltersSheetOpen(true);
              }}
              aria-label={t("common.filterLabel", "Szűrés")}
            >
              <Filter className="h-4 w-4" />
            </Button>
          ) : null}
          {isMobile ? (
            <Button
              variant="outline"
              size="sm"
              className="h-9 w-9 shrink-0 p-0"
              type="button"
              disabled={items.length === 0}
              onClick={() => {
                hapticTap(12);
                if (selectionModeEnabled) exitSelectionMode();
                else setSelectionModeEnabled(true);
              }}
              aria-label={selectionModeEnabled ? t("images.cancelSelection", "Kijelölés törlése") : t("manuals.selection", "Kiválasztás")}
            >
              {selectionModeEnabled ? <X className="h-4 w-4" /> : <CheckSquare className="h-4 w-4" />}
            </Button>
          ) : null}
          {!isMobile && selectedIds.size > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDeleteSelectedOpen(true)}
              className="text-destructive border-destructive/50 hover:bg-destructive/10"
              aria-label={`${t("priceGenerator.logDeleteSelectedButton", "Kijelöltek törlése")} (${selectedIds.size})`}
            >
              <Trash2 className="h-4 w-4 sm:mr-1.5" />
              <Badge variant="secondary" className="min-w-5 px-1 sm:hidden">
                {selectedIds.size}
              </Badge>
              <span className="hidden sm:inline">
                {t("priceGenerator.logDeleteSelectedButton", "Kijelöltek törlése")} ({selectedIds.size})
              </span>
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDeleteLogOpen(true)}
            aria-label={t("priceGenerator.logDeleteButton", "Napló törlése")}
          >
            <Trash2 className="h-4 w-4 sm:mr-1.5" />
            <span className="hidden sm:inline">{t("priceGenerator.logDeleteButton", "Napló törlése")}</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setValidateOpen(true);
              setValidateResult(null);
            }}
            aria-label={t("priceGenerator.logValidateButton", "Validáció")}
          >
            <RefreshCw className="h-4 w-4 sm:mr-1.5" />
            <span className="hidden sm:inline">{t("priceGenerator.logValidateButton", "Validáció")}</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setBulkOpen(true);
              setBulkResult(null);
            }}
            aria-label={t("priceGenerator.logBulkTestButton", "Bulk teszt")}
          >
            <FlaskConical className="h-4 w-4 sm:mr-1.5" />
            <span className="hidden sm:inline">{t("priceGenerator.logBulkTestButton", "Bulk teszt")}</span>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 min-w-0">
        {isMobile ? (
          <Sheet open={logFiltersSheetOpen} onOpenChange={setLogFiltersSheetOpen}>
            <SheetContent side="bottom" className="flex max-h-[90vh] flex-col gap-0">
              <SheetHeader className="space-y-1 text-left">
                <SheetTitle>{t("common.filterLabel", "Szűrés")}</SheetTitle>
              </SheetHeader>
              <div className="min-h-0 flex-1 overflow-y-auto">
                <div className="flex flex-col gap-4 py-3">{renderConfiguratorLogFilterFields()}</div>
              </div>
              <Button type="button" onClick={applyLogFilters} size="sm" className="mt-2 w-full shrink-0">
                {t("common.apply", "Alkalmaz")}
              </Button>
            </SheetContent>
          </Sheet>
        ) : (
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-4 items-stretch sm:items-end">
            {renderConfiguratorLogFilterFields()}
            <Button type="button" onClick={applyLogFilters} size="sm" className="w-full sm:w-auto shrink-0">
              {t("common.apply", "Alkalmaz")}
            </Button>
          </div>
        )}

        {reviewError && (
          <div className="rounded-md border border-orange-300 bg-orange-50 dark:bg-orange-900/20 dark:border-orange-700 p-3 text-sm text-orange-800 dark:text-orange-200 flex items-start gap-2">
            <span className="shrink-0 mt-0.5">⚠️</span>
            <div>
              <p className="font-medium">{t("priceGenerator.logReviewMigrationNeeded", "Migráció szükséges")}</p>
              <p className="text-xs mt-0.5">{reviewError}</p>
            </div>
            <button type="button" onClick={() => setReviewError(null)} className="ml-auto text-orange-600 hover:text-orange-800 dark:text-orange-300">×</button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : items.length === 0 ? (
          <p className="text-muted-foreground py-8 text-center">
            {t("priceGenerator.logEmpty", "Nincs ilyen log bejegyzés.")}
          </p>
        ) : (
          <>
            <div className="min-w-0 overflow-x-auto overscroll-x-contain touch-pan-x rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    {selectionModeEnabled ? (
                      <TableHead className="w-10 p-1">
                        <div className="min-h-9 min-w-9 flex items-center justify-center">
                          <Checkbox
                            size="2xs"
                            checked={allOnPageSelected ? true : someOnPageSelected ? "indeterminate" : false}
                            onCheckedChange={(checked) => selectAllOnPage(checked === true)}
                            aria-label={t("priceGenerator.logSelectAll", "Összes kijelölése az oldalon")}
                          />
                        </div>
                      </TableHead>
                    ) : null}
                    <TableHead className="w-10 p-1" />
                    <TableHead>{t("priceGenerator.logCreatedAt", "Időpont")}</TableHead>
                    <TableHead>{t("priceGenerator.logSource", "Forrás")}</TableHead>
                    <TableHead>{t("priceGenerator.logEventType", "Esemény")}</TableHead>
                    <TableHead>{t("priceGenerator.logEmail", "Email")}</TableHead>
                    <TableHead>{t("priceGenerator.logDimensions", "Méret")}</TableHead>
                    <TableHead>{t("priceGenerator.logMotiv", "Prelis")}</TableHead>
                    <TableHead>{t("priceGenerator.logSurface", "Povrch")}</TableHead>
                    <TableHead>{t("priceGenerator.logColor", "Szín")}</TableHead>
                    <TableHead>{t("priceGenerator.logElectric", "Pohon")}</TableHead>
                    <TableHead>{t("priceGenerator.logSolution", "Megoldás")}</TableHead>
                    <TableHead>{t("priceGenerator.logDeal", "Üzlet")}</TableHead>
                    <TableHead className="w-20" title={t("priceGenerator.logQuoteEmailSentTitle", "Árajánlat PDF email kiküldve")}>
                      {t("priceGenerator.logQuoteEmailSent", "Email")}
                    </TableHead>
                    <TableHead>{t("priceGenerator.logError", "Hiba")}</TableHead>
                    <TableHead className="w-16">{t("priceGenerator.logReviewLabel", "Értékelés")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((row) => (
                    <Fragment key={row.id}>
                      <TableRow
                        className={cn(
                          expandedRowId === row.id
                            ? "bg-muted/50"
                            : row.review_status === "correct"
                              ? "bg-green-50/50 dark:bg-green-900/10"
                              : row.review_status === "incorrect"
                                ? "bg-red-50/50 dark:bg-red-900/10"
                                : "",
                          selectionModeEnabled && "cursor-pointer",
                        )}
                        onPointerDown={(e) => beginRowLongPress(row.id, e)}
                        onPointerMove={(e) => moveRowLongPress(row.id, e)}
                        onPointerUp={() => cancelRowLongPress(row.id)}
                        onPointerCancel={() => cancelRowLongPress(row.id)}
                        onClick={() => {
                          if (!selectionModeEnabled) return;
                          toggleSelected(row.id);
                        }}
                      >
                        {selectionModeEnabled ? (
                          <TableCell className="w-10 p-1 align-middle" onClick={(e) => e.stopPropagation()}>
                            <div className="min-h-9 min-w-9 flex items-center justify-center">
                              <Checkbox
                                size="2xs"
                                checked={selectedIds.has(row.id)}
                                onCheckedChange={() => toggleSelected(row.id)}
                                aria-label={t("priceGenerator.logSelectRow", "Sor kijelölése")}
                              />
                            </div>
                          </TableCell>
                        ) : null}
                        <TableCell className="w-10 p-1 align-middle">
                          <button
                            type="button"
                            onClick={() => setExpandedRowId((id) => (id === row.id ? null : row.id))}
                            className="min-h-9 min-w-9 inline-flex items-center justify-center rounded hover:bg-muted"
                            aria-label={expandedRowId === row.id ? t("priceGenerator.logCollapseRules", "Szabályok összecsukása") : t("priceGenerator.logExpandRules", "Szabályok megjelenítése")}
                          >
                            {expandedRowId === row.id ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </button>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-xs">
                          {formatDate(row.created_at)}
                        </TableCell>
                      <TableCell className="text-xs">{t(`priceGenerator.logSourceValue.${row.source}`, row.source)}</TableCell>
                      <TableCell className="text-xs">{t(`priceGenerator.logEvent.${row.event_type}`, row.event_type)}</TableCell>
                      <TableCell className="text-xs max-w-[140px] truncate" title={row.email ?? ""}>
                        {row.email ?? "—"}
                      </TableCell>
                      <TableCell className="text-xs">
                        {row.width != null && row.height != null ? `${row.width}×${row.height}` : "—"}
                      </TableCell>
                      <TableCell className="text-xs">{row.motiv ?? "—"}</TableCell>
                      <TableCell className="text-xs">{row.surface ?? "—"}</TableCell>
                      <TableCell className="text-xs">{row.color ?? "—"}</TableCell>
                      <TableCell className="text-xs">{row.electric_drive ? t("common.yes", "Igen") : "—"}</TableCell>
                      <TableCell className="text-xs max-w-[200px]" title={row.solution_summary ?? ""}>
                        {row.solution_summary ?? (row.matched_width != null && row.matched_height != null ? `${row.matched_width}×${row.matched_height}` : "—")}
                      </TableCell>
                      <TableCell>
                        {row.deal_id ? (
                          <Link
                            to={`/crm/deals/${row.deal_id}`}
                            className="text-primary hover:underline text-xs"
                            title={row.deal_id}
                          >
                            {row.deal_title || `${row.deal_id.slice(0, 8)}…`}
                          </Link>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className="text-xs" title={row.quote_email_sent === true ? t("priceGenerator.logQuoteEmailSentYes", "Kiküldve") : row.quote_email_sent === false ? t("priceGenerator.logQuoteEmailSentNo", "Nem kiküldve / hiba") : ""}>
                        {row.quote_email_sent === true ? (
                          <span className="text-green-600 dark:text-green-400" aria-label={t("priceGenerator.logQuoteEmailSentYes", "Kiküldve")}>✓</span>
                        ) : row.quote_email_sent === false ? (
                          <span className="text-amber-600 dark:text-amber-400" aria-label={t("priceGenerator.logQuoteEmailSentNo", "Nem kiküldve")}>✗</span>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className="text-xs max-w-[180px] truncate text-destructive" title={row.error_message ?? ""}>
                        {row.error_message ?? "—"}
                      </TableCell>
                      <TableCell>
                        <ReviewButtons
                          reviewStatus={row.review_status}
                          onToggle={(status) => handleReviewUpdate(row.id, status)}
                        />
                      </TableCell>
                    </TableRow>
                    {expandedRowId === row.id && (
                      <TableRow className="bg-muted/30">
                        <TableCell colSpan={16} className="p-4">
                          {row.rule_steps && row.rule_steps.length > 0 && (
                            <>
                              <div className="text-xs font-medium mb-2">
                                {t("priceGenerator.logRuleStepsTitle", "Kiértékelte szabályok")}
                              </div>
                              <ul className="space-y-2">
                                {row.rule_steps.map((s, i) => (
                                  <li key={i} className="flex flex-col gap-1 border-b border-border/50 pb-2 last:border-0">
                                    <div className="flex flex-wrap items-baseline gap-2">
                                      <span className="font-mono text-muted-foreground">{s.step}</span>
                                      <span>{s.description}</span>
                                      <span className={s.result === "match" ? "text-green-600 dark:text-green-400 font-medium" : "text-muted-foreground"}>
                                        → {s.result === "match" && s.matched_width != null && s.matched_height != null
                                          ? `${s.matched_width}×${s.matched_height} mm`
                                          : s.result}
                                      </span>
                                      {s.score != null && (
                                        <span className="text-xs text-blue-600 dark:text-blue-400 font-mono">
                                          (score: {s.score.toFixed(1)})
                                        </span>
                                      )}
                                    </div>
                                    {s.explanation && (
                                      <p className="text-muted-foreground pl-2 border-l-2 border-muted">
                                        {s.explanation}
                                      </p>
                                    )}
                                    {s.candidates && s.candidates.length > 0 && (
                                      <div className="pl-2 border-l-2 border-blue-200 dark:border-blue-800 mt-1">
                                        <span className="text-[10px] font-medium text-blue-600 dark:text-blue-400">
                                          Top {s.candidates.length}:
                                        </span>
                                        <div className="flex flex-wrap gap-2 mt-0.5">
                                          {s.candidates.map((c, ci) => (
                                            <span key={ci} className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${ci === 0 ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300" : "bg-muted text-muted-foreground"}`}>
                                              {ci + 1}. {c.sirka}×{c.vyska} = {c.score.toFixed(1)} pt (w:{c.wDist}×{c.wPenalty}, h:{c.hDist}×{c.hPenalty})
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </>
                          )}
                          {row.quote_items && row.quote_items.length > 0 && (
                            <div className={row.rule_steps && row.rule_steps.length > 0 ? "mt-3 pt-2 border-t border-border/50" : ""}>
                              <div className="text-xs font-medium mb-2">
                                {t("priceGenerator.logQuoteItemsTitle", "Termékek (ár)")}
                              </div>
                              <div className="overflow-x-auto rounded border border-border/50">
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead className="text-xs">{t("priceGenerator.logQuoteItemName", "Položka")}</TableHead>
                                      <TableHead className="text-xs text-right">{t("priceGenerator.logQuoteItemPrice", "Jedn. cena")}</TableHead>
                                      <TableHead className="text-xs text-right">{t("priceGenerator.logQuoteItemQty", "Množstvo")}</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {row.quote_items.map((item, idx) => (
                                      <TableRow key={idx}>
                                        <TableCell className="text-xs py-1">{item.item_name}{item.comment ? ` — ${item.comment}` : ""}</TableCell>
                                        <TableCell className="text-xs text-right py-1">{item.price != null ? `${Number(item.price).toFixed(2)} €` : "—"}</TableCell>
                                        <TableCell className="text-xs text-right py-1">{item.quantity ?? 1}{item.quantity_text ? ` ${item.quantity_text}` : ""}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                            </div>
                          )}
                          <div className={row.rule_steps && row.rule_steps.length > 0 ? "mt-3 pt-2 border-t border-border/50" : ""}>
                            <ReviewNoteEditor
                              reviewNote={row.review_note}
                              onSave={(note) => handleReviewNoteSave(row.id, note)}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between text-sm text-muted-foreground">
              <span className="text-center sm:text-left">
                {t("priceGenerator.logPagination", "Összesen {{total}} sor", { total })}
              </span>
              <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span>
                  {t("priceGenerator.logPage", "{{current}} / {{total}}", {
                    current: page,
                    total: totalPages,
                  })}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("priceGenerator.logBulkTestTitle", "Bulk teszt generálás")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              {t("priceGenerator.logBulkTestRandomNote", "Minden tételhez véletlenszerű méret, Prelis (M/L), Povrch és elektromos hajtás kerül kisorsolásra.")}
            </p>
            <div className="space-y-1">
              <Label className="text-xs">{t("priceGenerator.logBulkTestCount", "Darabszám")}</Label>
              <Input type="number" min={1} max={500} value={bulkCount} onChange={(e) => setBulkCount(e.target.value)} />
            </div>
            {bulkResult && (
              <div className="rounded-md border bg-muted/30 p-3 text-xs space-y-0.5">
                <p>{t("priceGenerator.logBulkTestGenerated", "Generált")}: <strong>{bulkResult.generated}</strong></p>
                <p className="text-green-600">{t("priceGenerator.logBulkTestMatched", "Találat")}: <strong>{bulkResult.matched}</strong></p>
                <p className="text-orange-600">{t("priceGenerator.logBulkTestNoMatch", "Nincs találat")}: <strong>{bulkResult.noMatch}</strong></p>
                {bulkResult.noMatchBreakdown && bulkResult.noMatch > 0 && (
                  <p className="text-muted-foreground pl-2">
                    {t("priceGenerator.logBulkTestNoMatchBreakdown", "Ebből: túl kicsi")} <strong>{bulkResult.noMatchBreakdown.size_too_small}</strong>, {t("priceGenerator.logBulkTestNoMatchBreakdown2", "túl nagy")} <strong>{bulkResult.noMatchBreakdown.size_too_large}</strong>, {t("priceGenerator.logBulkTestNoMatchBreakdown3", "nincs ár")} <strong>{bulkResult.noMatchBreakdown.no_match}</strong>
                  </p>
                )}
                {(bulkResult.insertFailed ?? 0) > 0 && (
                  <div className="text-red-600 space-y-0.5">
                    <p>
                      {t("priceGenerator.logBulkTestInsertFailed", "Mentés sikertelen")}: <strong>{bulkResult.insertFailed}</strong> — {t("priceGenerator.logBulkTestInsertFailedHint", "ellenőrizd, hogy a migrációk le vannak-e futtatva (097, 098, 100)")}
                    </p>
                    {bulkResult.firstInsertError && (
                      <p className="text-xs text-red-500 break-all">Hiba: {bulkResult.firstInsertError}</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)} disabled={bulkRunning}>
              {t("common.cancel", "Mégse")}
            </Button>
            <Button onClick={handleBulkTest} disabled={bulkRunning}>
              {bulkRunning && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("priceGenerator.logBulkTestGenerate", "Generálás")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteLogOpen} onOpenChange={setDeleteLogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("priceGenerator.logDeleteTitle", "Napló törlése")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("priceGenerator.logDeleteConfirm", "Biztosan törlöd a teljes konfigurátor naplót? Ez visszavonhatatlan.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteLogRunning}>{t("common.cancel", "Mégse")}</AlertDialogCancel>
            <Button variant="destructive" onClick={handleDeleteLog} disabled={deleteLogRunning}>
              {deleteLogRunning && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("priceGenerator.logDeleteConfirmButton", "Törlés")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteSelectedOpen} onOpenChange={setDeleteSelectedOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("priceGenerator.logDeleteSelectedTitle", "Kijelöltek törlése")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("priceGenerator.logDeleteSelectedConfirm", "Biztosan törlöd a {{count}} kijelölt bejegyzést? Ez visszavonhatatlan.", { count: selectedIds.size })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteSelectedRunning}>{t("common.cancel", "Mégse")}</AlertDialogCancel>
            <Button variant="destructive" onClick={handleDeleteSelected} disabled={deleteSelectedRunning}>
              {deleteSelectedRunning && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("priceGenerator.logDeleteConfirmButton", "Törlés")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={validateOpen} onOpenChange={setValidateOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("priceGenerator.logValidateTitle", "Validáció — értékelt sorok újraszámolása")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              {t("priceGenerator.logValidateDesc", "Újraszámolja az összes 'helyes' és 'hibás' jelölésű log bejegyzést a jelenlegi beállításokkal, és összehasonlítja az eredeti eredménnyel.")}
            </p>
            {!validateResult && (
              <Button onClick={handleValidateReviews} disabled={validateRunning}>
                {validateRunning && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t("priceGenerator.logValidateRun", "Validáció futtatása")}
              </Button>
            )}
            {validateResult && (
              <div className="space-y-3">
                <div className="rounded-md border bg-muted/30 p-3 text-xs space-y-0.5">
                  <p>{t("priceGenerator.logValidateTotal", "Összes értékelt")}: <strong>{validateResult.summary.total}</strong></p>
                  <p className="text-green-600">{t("priceGenerator.logValidateStillCorrect", "Továbbra is helyes")}: <strong>{validateResult.summary.still_correct}</strong></p>
                  <p className="text-orange-600">{t("priceGenerator.logValidateStillIncorrect", "Továbbra is hibás")}: <strong>{validateResult.summary.still_incorrect}</strong></p>
                  <p className="text-blue-600">{t("priceGenerator.logValidateChanged", "Változott")}: <strong>{validateResult.summary.changed}</strong></p>
                  {validateResult.summary.now_correct > 0 && (
                    <p className="text-green-700 pl-2">{t("priceGenerator.logValidateNowCorrect", "Korábban hibás → most más eredmény (javult?)")}: <strong>{validateResult.summary.now_correct}</strong></p>
                  )}
                  {validateResult.summary.now_incorrect > 0 && (
                    <p className="text-red-600 pl-2">{t("priceGenerator.logValidateNowIncorrect", "Korábban helyes → most más eredmény (romlott!)")}: <strong>{validateResult.summary.now_incorrect}</strong></p>
                  )}
                </div>
                {validateResult.results.filter((r) => r.changed).length > 0 && (
                  <div>
                    <h4 className="text-xs font-medium mb-1">{t("priceGenerator.logValidateChangedRows", "Változott sorok")}:</h4>
                    <div className="overflow-x-auto rounded border border-border/50 max-h-[300px] overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-[10px]">{t("priceGenerator.logWidth", "Szélesség")}</TableHead>
                            <TableHead className="text-[10px]">{t("priceGenerator.logHeight", "Magasság")}</TableHead>
                            <TableHead className="text-[10px]">{t("priceGenerator.logValidateOrig", "Eredeti")}</TableHead>
                            <TableHead className="text-[10px]">{t("priceGenerator.logValidateNew", "Új")}</TableHead>
                            <TableHead className="text-[10px]">{t("priceGenerator.logReviewLabel", "Értékelés")}</TableHead>
                            <TableHead className="text-[10px]">{t("priceGenerator.logValidateVerdict", "Ítélet")}</TableHead>
                            <TableHead className="text-[10px]">{t("priceGenerator.logReviewNote", "Megjegyzés")}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {validateResult.results.filter((r) => r.changed).map((r) => (
                            <TableRow key={r.id}>
                              <TableCell className="text-[10px] py-1">{r.width}</TableCell>
                              <TableCell className="text-[10px] py-1">{r.height}</TableCell>
                              <TableCell className="text-[10px] py-1">{r.original.matched_width && r.original.matched_height ? `${r.original.matched_width}×${r.original.matched_height}` : "—"} ({r.original.result_type ?? "—"})</TableCell>
                              <TableCell className="text-[10px] py-1">{r.recalculated.matched_width && r.recalculated.matched_height ? `${r.recalculated.matched_width}×${r.recalculated.matched_height}` : "—"} ({r.recalculated.result_type ?? "—"})</TableCell>
                              <TableCell className="text-[10px] py-1">
                                <span className={r.review_status === "correct" ? "text-green-600" : "text-red-600"}>{r.review_status}</span>
                              </TableCell>
                              <TableCell className="text-[10px] py-1">
                                <span className={r.verdict === "now_correct" ? "text-green-700 font-medium" : r.verdict === "now_incorrect" ? "text-red-600 font-medium" : ""}>
                                  {r.verdict}
                                </span>
                              </TableCell>
                              <TableCell className="text-[10px] py-1 max-w-[200px] truncate">{r.review_note ?? "—"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setValidateOpen(false)}>
              {t("common.close", "Bezárás")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {isMobile && selectionModeEnabled ? (
        <div className="fixed inset-x-0 z-40 px-3 pb-2" style={{ bottom: "calc(4rem + env(safe-area-inset-bottom))" }}>
          <div className="mx-auto flex max-w-xl flex-wrap items-center gap-2 rounded-xl border bg-background/95 px-3 py-2 shadow-lg backdrop-blur">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9 w-9 shrink-0 p-0"
                aria-label={t("images.cancelSelection", "Kijelölés törlése")}
                onClick={exitSelectionMode}
              >
                <X className="h-4 w-4" />
              </Button>
              <span className="max-w-[10rem] truncate text-sm text-muted-foreground sm:max-w-none">
                {selectedIds.size} {t("images.selected", "kijelölve")}
              </span>
              {selectedIds.size === 0 ? (
                <span className="text-sm text-muted-foreground">{t("common.selectItemsHint")}</span>
              ) : null}
              <button
                type="button"
                className="shrink-0 text-sm text-primary underline-offset-4 hover:underline disabled:pointer-events-none disabled:opacity-50 hidden sm:inline"
                disabled={items.length === 0}
                onClick={() => setSelectedIds(new Set(items.map((r) => r.id)))}
              >
                {t("common.selectAll")}
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-9 w-9 shrink-0 p-0 ml-auto" aria-label={t("common.more")}>
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => setSelectedIds(new Set(items.map((r) => r.id)))}
                    disabled={items.length === 0}
                  >
                    {t("common.selectAll")}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => setDeleteSelectedOpen(true)}
                    disabled={selectedIds.size === 0}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {t("priceGenerator.logDeleteSelectedButton", "Kijelöltek törlése")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
          </div>
        </div>
      ) : null}
    </Card>
      </TabsContent>
      <TabsContent value="ai" className="mt-0 border-0 p-0 shadow-none outline-none">
        <AiClassificationsLogContent />
      </TabsContent>
      <TabsContent value="facebook_webhooks" className="mt-0 border-0 p-0 shadow-none outline-none">
        <div className="space-y-4">
          <LeadListPanel unified />
          <WebhookHistory />
        </div>
      </TabsContent>
    </Tabs>
  );
}
